import { autoSignIn, confirmSignUp, resendSignUpCode, signUp } from "aws-amplify/auth";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../components/AuthLayout";
import { FeeCalculator } from "../components/FeeCalculator";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { pendingInvite, useSession } from "../lib/session";

export function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useSession();
  const { pending, error, run } = useAction();

  // ?confirm=<email> arrives from the login page for an account that was never confirmed.
  const confirmOnly = params.get("confirm");
  const [email, setEmail] = useState(confirmOnly ?? params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<"details" | "code">(confirmOnly ? "code" : "details");
  const [code, setCode] = useState("");
  const [resent, setResent] = useState(false);

  const submitDetails = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { nextStep } = await signUp({
        username: email.trim().toLowerCase(),
        password,
        options: { userAttributes: { email: email.trim().toLowerCase() }, autoSignIn: true },
      });
      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") setStep("code");
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { nextStep } = await confirmSignUp({ username: email.trim().toLowerCase(), confirmationCode: code.trim() });
      if (nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        await autoSignIn();
        await refresh();
        navigate(pendingInvite.get() ? "/join" : "/setup", { replace: true });
      } else {
        // Confirmed from a fresh session (no password in memory): log in normally.
        navigate(`/login?email=${encodeURIComponent(email.trim().toLowerCase())}`, { replace: true });
      }
    });
  };

  const resend = () =>
    void run(async () => {
      await resendSignUpCode({ username: email.trim().toLowerCase() });
      setResent(true);
    });

  return (
    <AuthLayout
      masthead={
        <div className="stack-lg">
          <h1 className="display">
            Your fees, <em>yours</em>.
          </h1>
          <FeeCalculator />
        </div>
      }
    >
      {step === "details" ? (
        <form className="form" onSubmit={submitDetails}>
          <div className="page-head">
            <p className="label">Step 1 of 2</p>
            <h2 className="title">
              Create your <em>login</em>
            </h2>
          </div>
          <Field label="Work email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="12+ characters, with upper and lower case, a number and a symbol."
          />
          <ErrorNotice error={error} />
          <button className="btn block" disabled={pending}>
            {pending ? "Creating…" : "Continue"}
          </button>
          <p className="sheet-foot">
            Already have a login? <Link to={`/login${params.toString() ? `?${params}` : ""}`}>Log in</Link>
          </p>
        </form>
      ) : (
        <form className="form" onSubmit={submitCode}>
          <div className="page-head">
            <p className="label">Step 2 of 2</p>
            <h2 className="title">
              Check your <em>inbox</em>
            </h2>
            <p className="lede">
              We sent a 6-digit code to <strong>{email}</strong>.
            </p>
          </div>
          <Field
            label="Verification code"
            className="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <ErrorNotice error={error} />
          {resent && <p className="notice ok">A new code is on its way.</p>}
          <button className="btn block" disabled={pending}>
            {pending ? "Verifying…" : "Verify email"}
          </button>
          <p className="sheet-foot">
            No email?{" "}
            <button type="button" className="link-btn" onClick={resend} disabled={pending}>
              Send another code
            </button>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
